import {
  quatRotateVec3,
  UP,
  type Quat,
  type Vec3,
  type Vehicle,
} from '@polyloop/sim';

/**
 * Trick / score tracker for the local player.
 *
 *  - Airtime  — +5 pts / second airborne.
 *  - Long Jump — +200 bonus on landing if airTime > 1.5s.
 *  - Flip — +500 if the car went upside-down then landed upright.
 *  - Drift — +1 pt per 100ms while drifting at speed > 6 m/s.
 *  - Boost pad — +50 on every boost-pad entry.
 *  - Bump — +25 every time you hit another car (only when you initiate it,
 *           gauged by speedDelta and surface contact).
 */
export interface TrickEvent {
  kind: 'flip' | 'long-jump' | 'boost' | 'bump' | 'air';
  label: string;
  points: number;
  /** performance.now() at emission time, for fade-out timing. */
  at: number;
}

const tmpUp: Vec3 = { x: 0, y: 0, z: 0 };

export class TrickTracker {
  score = 0;
  events: TrickEvent[] = [];

  private wasGrounded = true;
  private airTimeAccumMs = 0;
  private airTimeAwardedMs = 0;
  private upsideDownReached = false;
  private prevSurface: string = 'road';
  private driftAccumMs = 0;

  step(vehicle: Vehicle, dtSec: number): void {
    const dtMs = dtSec * 1000;
    const now = performance.now();
    const grounded = vehicle.isGrounded;
    const surface = vehicle.surface;

    // Airborne integration.
    if (!grounded) {
      // Award airtime continuously: 5 pts / sec.
      this.airTimeAccumMs += dtMs;
      const totalEarned = Math.floor(this.airTimeAccumMs / 200); // 5/sec
      const newEarned = totalEarned - Math.floor(this.airTimeAwardedMs / 200);
      if (newEarned > 0) {
        this.score += newEarned;
        this.airTimeAwardedMs = totalEarned * 200;
      }

      // Check if we're upside-down at any point during this flight.
      quatRotateVec3(tmpUp, vehicle.quaternion as Quat, UP as Vec3);
      if (tmpUp.y < -0.35) this.upsideDownReached = true;
    } else {
      // Landing this frame?
      if (!this.wasGrounded) {
        const totalAir = this.airTimeAccumMs;
        if (this.upsideDownReached) {
          this.score += 500;
          this.events.push({ kind: 'flip', label: 'FLIP!', points: 500, at: now });
        }
        if (totalAir > 1500) {
          this.score += 200;
          this.events.push({ kind: 'long-jump', label: 'LONG JUMP', points: 200, at: now });
        } else if (totalAir > 600) {
          // Mid-jump nudge — encourages going for air without spam.
          this.score += 25;
          this.events.push({ kind: 'air', label: 'AIR', points: 25, at: now });
        }
        this.airTimeAccumMs = 0;
        this.airTimeAwardedMs = 0;
        this.upsideDownReached = false;
      }
    }

    // Drift integration.
    if (grounded && vehicle.driftPower > 8) {
      this.driftAccumMs += dtMs;
      if (this.driftAccumMs >= 100) {
        const pts = Math.floor(this.driftAccumMs / 100);
        this.score += pts;
        this.driftAccumMs -= pts * 100;
      }
    } else {
      this.driftAccumMs = 0;
    }

    // Boost pad — award on entry.
    if (surface === 'boost' && this.prevSurface !== 'boost') {
      this.score += 50;
      this.events.push({ kind: 'boost', label: 'BOOST PAD', points: 50, at: now });
    }
    this.prevSurface = surface;

    // Car-vs-car bump — award when ours is set (sim writes it on each hit).
    if (vehicle.lastCarHit) {
      const speedDelta = vehicle.lastCarHit.speedDelta;
      // Only score "meaningful" bumps; tiny taps are ignored.
      if (speedDelta > 1.5) {
        this.score += 25;
        this.events.push({ kind: 'bump', label: 'BUMP!', points: 25, at: now });
      }
      vehicle.lastCarHit = null;
    }

    this.wasGrounded = grounded;

    // Prune old events (HUD reads, then we drop after 1.6s).
    if (this.events.length > 0) {
      const cutoff = now - 1600;
      this.events = this.events.filter((e) => e.at > cutoff);
    }
  }

  reset(): void {
    this.score = 0;
    this.events = [];
    this.airTimeAccumMs = 0;
    this.airTimeAwardedMs = 0;
    this.upsideDownReached = false;
    this.driftAccumMs = 0;
    this.wasGrounded = true;
    this.prevSurface = 'road';
  }
}
